import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./RoomLayout.scss";
import { Toolbar } from "./Toolbar";
import { ToolbarProvider } from "../contexts/ToolbarContext";
import configManager from "../../singleton/ConfigManager";
import { Badge } from "@fluentui/react-components";
import { Notch } from "../room/Notch";
import { TranscriptionCaptionOverlay } from "../transcription/TranscriptionCaptionOverlay";
import {
  ParticipantLayoutContainer,
  useParticipantLayoutImmersiveMode
} from "./participant-layout/ParticipantLayoutContainer";
import { ParticipantLayoutActiveProvider } from "./participant-layout/ParticipantLayoutContext";
import { PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME } from "./toolbar-more-button-items";
import {
  PARTICIPANT_LAYOUT_FORCED_STATE,
  createHubSettingsForParticipantLayoutRoomState,
  createParticipantLayoutRoomStateForForcedStateChange,
  createParticipantLayoutRoomStateForSpotlightChange,
  getParticipantLayoutRoomStateFromConfigAndHub,
  normalizeParticipantLayoutForcedState,
  normalizeParticipantLayoutSpotlightTileId
} from "./participant-layout/participant-layout-room-state";

export function RoomLayout({
  className,
  viewportClassName,
  sidebar,
  sidebarClassName,
  sidebarLeft,
  modal,
  viewport,
  hub,
  objectFocused,
  isNotAbsolute,
  streaming,
  viewportRef,
  toggleStreamerMode,
  showToolbar = true,
  renderEntryFlow,
  entered = false,
  scene,
  hubChannel,
  signedIn,
  dialogConnected,
  hasDialogConnectedOnce,
  performConditionalSignIn,
  ...rest
}) {
  const getInitialParticipantLayoutState = () =>
    getParticipantLayoutRoomStateFromConfigAndHub(configManager.config.participantLayout, hub);
  const [participantLayoutRoomEnabled, setParticipantLayoutRoomEnabled] = useState(
    () => getInitialParticipantLayoutState().roomEnabled
  );
  const [participantLayoutEnabled, setParticipantLayoutEnabled] = useState(
    () => getInitialParticipantLayoutState().layoutEnabled
  );
  const [participantLayoutForcedState, setParticipantLayoutForcedState] = useState(
    () => getInitialParticipantLayoutState().forcedState
  );
  const [participantLayoutSpotlightTileId, setParticipantLayoutSpotlightTileId] = useState(
    () => getInitialParticipantLayoutState().spotlightTileId
  );
  const participantLayoutForced = participantLayoutForcedState !== PARTICIPANT_LAYOUT_FORCED_STATE.NONE;
  const participantLayoutForcedOpen = participantLayoutForcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN;
  const participantLayoutImmersiveModeActive = useParticipantLayoutImmersiveMode(scene);
  const participantLayoutActive = participantLayoutEnabled && !participantLayoutImmersiveModeActive;
  const participantLayoutConfigEnabled = !!configManager.config.participantLayout?.enabled;
  const participantLayoutConfigForceEnabled = !!configManager.config.participantLayout?.forceEnabled;
  const participantLayoutRoomSettingsManageable =
    participantLayoutConfigEnabled && !participantLayoutConfigForceEnabled;
  const showVerseSettings =
    configManager.config.roomEntryDialog?.roomSettings?.enabled &&
    hubChannel &&
    hubChannel.canOrWillIfCreator("update_hub") &&
    signedIn;

  const syncParticipantLayoutRoomStateFromHub = useCallback(nextHub => {
    const { roomEnabled, forcedState, spotlightTileId } = getParticipantLayoutRoomStateFromConfigAndHub(
      configManager.config.participantLayout,
      nextHub
    );

    setParticipantLayoutRoomEnabled(roomEnabled);
    setParticipantLayoutForcedState(forcedState);
    setParticipantLayoutSpotlightTileId(spotlightTileId);

    if (!roomEnabled || forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED) {
      setParticipantLayoutEnabled(false);
    } else if (forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN) {
      setParticipantLayoutEnabled(true);
    }
  }, []);

  useEffect(() => {
    syncParticipantLayoutRoomStateFromHub(hub);
  }, [hub, syncParticipantLayoutRoomStateFromHub]);

  useEffect(() => {
    if (participantLayoutEnabled && streaming && toggleStreamerMode) {
      toggleStreamerMode();
    }
  }, [participantLayoutEnabled, streaming, toggleStreamerMode]);

  useEffect(() => {
    if (!scene) {
      return undefined;
    }

    const onHubUpdated = event => {
      const currentHub = typeof window !== "undefined" ? window.APP?.hub : null;
      syncParticipantLayoutRoomStateFromHub(event.detail?.hub || currentHub || hub);
    };

    scene.addEventListener("hub_updated", onHubUpdated);

    return () => {
      scene.removeEventListener("hub_updated", onHubUpdated);
    };
  }, [hub, scene, syncParticipantLayoutRoomStateFromHub]);

  const toggleParticipantLayout = () => {
    if (participantLayoutForced || !participantLayoutRoomEnabled) {
      return;
    }

    setParticipantLayoutEnabled(previousEnabled => !previousEnabled);
  };

  const setParticipantLayoutEnabledFromContainer = nextEnabled => {
    if (participantLayoutForced || !participantLayoutRoomEnabled) {
      return;
    }

    setParticipantLayoutEnabled(nextEnabled);
  };

  const updateSharedParticipantLayoutRoomState = nextRoomState => {
    const hasRoomEnabled = Object.prototype.hasOwnProperty.call(nextRoomState, "roomEnabled");
    const hasForcedState = Object.prototype.hasOwnProperty.call(nextRoomState, "forcedState");
    const hasSpotlightTileId = Object.prototype.hasOwnProperty.call(nextRoomState, "spotlightTileId");
    const roomEnabled = hasRoomEnabled ? !!nextRoomState.roomEnabled : participantLayoutRoomEnabled;
    const forcedState = roomEnabled
      ? normalizeParticipantLayoutForcedState(hasForcedState ? nextRoomState.forcedState : participantLayoutForcedState)
      : PARTICIPANT_LAYOUT_FORCED_STATE.NONE;
    const spotlightTileId = roomEnabled
      ? normalizeParticipantLayoutSpotlightTileId(
          hasSpotlightTileId ? nextRoomState.spotlightTileId : participantLayoutSpotlightTileId
        )
      : null;

    setParticipantLayoutRoomEnabled(roomEnabled);
    setParticipantLayoutForcedState(forcedState);
    setParticipantLayoutSpotlightTileId(spotlightTileId);

    if (!roomEnabled || forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.CLOSED) {
      setParticipantLayoutEnabled(false);
    } else if (forcedState === PARTICIPANT_LAYOUT_FORCED_STATE.OPEN) {
      setParticipantLayoutEnabled(true);
    }

    const currentHub = typeof window !== "undefined" ? window.APP?.hub || hub : hub;

    if (!currentHub || !hubChannel?.can?.("update_hub")) {
      return;
    }

    hubChannel.updateHub(
      createHubSettingsForParticipantLayoutRoomState(currentHub, {
        roomEnabled,
        forcedState,
        spotlightTileId
      })
    );
  };

  const handleParticipantLayoutRoomSpotlightChange = spotlightTileId => {
    updateSharedParticipantLayoutRoomState(createParticipantLayoutRoomStateForSpotlightChange(spotlightTileId));
  };

  const handleParticipantLayoutManagementCheckedValueChange = (_, { name, checkedItems }) => {
    if (!participantLayoutRoomSettingsManageable) {
      return;
    }

    if (name === PARTICIPANT_LAYOUT_FORCED_STATE_MENU_NAME) {
      const [nextForcedState = PARTICIPANT_LAYOUT_FORCED_STATE.NONE] = checkedItems;

      updateSharedParticipantLayoutRoomState(
        createParticipantLayoutRoomStateForForcedStateChange(nextForcedState, {
          spotlightTileId: participantLayoutSpotlightTileId
        })
      );
    }
  };

  return (
    <div className={classNames(styles.roomLayout, { [styles.objectFocused]: objectFocused }, className)}>
      {sidebar && (
        <div
          className={classNames(styles.sidebarContainer, {
            [styles.isNotAbsolute]: isNotAbsolute,
            [styles.sidebarLeft]: sidebarLeft
          })}
        >
          <div className={classNames(styles.sidebar, sidebarClassName)}>{sidebar}</div>
        </div>
      )}
      <div className={classNames(styles.modalContainer, styles.viewport)}>{modal}</div>
      <div
        className={classNames(styles.main, styles.viewport, viewportClassName, "RoomLayoutViewport")}
        ref={viewportRef}
      >
        {streaming && (
          <div className={styles.streamingOverlay}>
            <Notch spaced onClose={toggleStreamerMode}>
              <Badge size="extra-small" color="danger" className={styles.streamingBadge} />
            </Notch>
          </div>
        )}

        <ParticipantLayoutActiveProvider
          active={participantLayoutActive}
          onRequestClose={
            participantLayoutForced || !participantLayoutRoomEnabled
              ? undefined
              : () => setParticipantLayoutEnabledFromContainer(false)
          }
        >
          <ParticipantLayoutContainer
            enabled={participantLayoutEnabled}
            onEnabledChange={setParticipantLayoutEnabledFromContainer}
            forcedEnabled={participantLayoutForcedOpen}
            entered={entered}
            hubChannel={hubChannel}
            performConditionalSignIn={performConditionalSignIn}
            roomSpotlightTileId={participantLayoutSpotlightTileId}
            onRoomSpotlightChange={handleParticipantLayoutRoomSpotlightChange}
            scene={scene}
            {...rest}
          />
          {viewport}
          <TranscriptionCaptionOverlay />
          <ToolbarProvider canSpawnEmojis={hubChannel && hubChannel.can("spawn_emoji")}>
            {!renderEntryFlow && showToolbar && (
              <Toolbar
                hubChannel={hubChannel}
                signedIn={signedIn}
                showVerseSettings={showVerseSettings}
                objectFocused={objectFocused}
                toggleStreamerMode={toggleStreamerMode}
                dialogConnected={dialogConnected}
                hasDialogConnectedOnce={hasDialogConnectedOnce}
                performConditionalSignIn={performConditionalSignIn}
                participantLayoutEnabled={participantLayoutEnabled}
                participantLayoutForced={participantLayoutForced}
                participantLayoutRoomEnabled={participantLayoutRoomEnabled}
                participantLayoutForcedState={participantLayoutForcedState}
                onParticipantLayoutToggle={toggleParticipantLayout}
                onParticipantLayoutRoomStateChange={
                  participantLayoutRoomSettingsManageable ? updateSharedParticipantLayoutRoomState : undefined
                }
                onParticipantLayoutManagementCheckedValueChange={handleParticipantLayoutManagementCheckedValueChange}
                scene={scene}
                {...rest}
              />
            )}
          </ToolbarProvider>
        </ParticipantLayoutActiveProvider>
      </div>
    </div>
  );
}

RoomLayout.propTypes = {
  className: PropTypes.string,
  viewportClassName: PropTypes.string,
  sidebar: PropTypes.node,
  sidebarClassName: PropTypes.string,
  sidebarLeft: PropTypes.bool,
  toolbarLeft: PropTypes.node,
  toolbarCenter: PropTypes.node,
  toolbarRight: PropTypes.node,
  toolbarClassName: PropTypes.string,
  modal: PropTypes.node,
  viewport: PropTypes.node,
  hub: PropTypes.object,
  objectFocused: PropTypes.bool,
  streaming: PropTypes.bool,
  viewportRef: PropTypes.any,
  toggleToolbar: PropTypes.node,
  showToolbar: PropTypes.bool,
  isNotAbsolute: PropTypes.bool,
  hubChannel: PropTypes.object,
  scene: PropTypes.object,
  signedIn: PropTypes.bool,
  dialogConnected: PropTypes.bool,
  hasDialogConnectedOnce: PropTypes.bool,
  entered: PropTypes.bool,
  renderEntryFlow: PropTypes.bool,
  toggleStreamerMode: PropTypes.func,
  performConditionalSignIn: PropTypes.func
};
